/**
 * Codes HTTP qui relèvent du fonctionnement normal, et non d'une défaillance.
 *
 * Le quota Bugsnag du compte « ID Synergy » est de 250 événements par jour. Il
 * en recevait près de 6 800, dont 6 582 rejetés quotidiennement — à cause d'une
 * seule erreur, `Non authentifié`, remontée à tort par quatre applications dont
 * le plugin ne testait pas le `statusCode`. Bugsnag n'y servait plus à rien :
 * une vraie erreur avait toutes les chances d'être jetée avant d'être vue.
 *
 * Adhérence lève **26** de ces 401 dans son code serveur. Sans ce filtre, elle
 * reproduirait exactement le bruit qu'on vient d'éteindre, et dans un quota
 * déjà saturé. Le filtre est donc posé avant le premier événement, plutôt
 * qu'après le débordement.
 *
 * Il ne s'agit pas de masquer des erreurs, mais d'écarter ce qui n'en est pas :
 *
 * - 401 : requête sans session valide. C'est la réponse attendue pour une
 *   session expirée ou un onglet resté ouvert — la cause exacte du débordement
 *   dans les quatre autres applications.
 * - 403 : session valide mais droits insuffisants. Réponse attendue, elle
 *   aussi : le contrôle d'accès fait son travail, et Adhérence en a un
 *   détaillé — adhérents, adhésions, demandes juridiques, export RNIC.
 * - 404 : adresse inexistante. Route d'API mal appelée, adhérent supprimé,
 *   scanners automatiques (`/.env`, `/wp-login.php`…) — du bruit de fond
 *   permanent. Un mauvais appel d'API interne reste visible côté appelant, dont
 *   l'erreur, elle, remonte.
 *
 * Tout le reste continue de remonter : une erreur de rendu, une exception non
 * rattrapée, un 500 de la base. C'est le code HTTP attendu qui distingue le
 * normal de l'anormal, pas le fait qu'une erreur soit rattrapée.
 */
export const CODES_HTTP_ATTENDUS = [401, 403, 404] as const

const codesAttendus = new Set<number>(CODES_HTTP_ATTENDUS)

/**
 * Lit le `statusCode` d'une erreur.
 *
 * Seules les erreurs issues de `createError()` — et les `FetchError` de
 * `$fetch` — en portent un. Une exception ordinaire n'en a pas : elle renvoie
 * `undefined`.
 *
 * `statusCode` est la propriété de Nuxt et de h3 ; `status` celle de l'API
 * `Response` du navigateur, que portent aussi les `FetchError` d'ofetch. Les
 * deux sont lues, la première trouvée l'emporte.
 */
export function statusCodeDeLErreur(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined

  const candidat = error as { statusCode?: unknown, status?: unknown }

  if (typeof candidat.statusCode === 'number') return candidat.statusCode
  if (typeof candidat.status === 'number') return candidat.status

  return undefined
}

/**
 * Décide si une erreur doit être envoyée à Bugsnag.
 *
 * Par défaut oui : une erreur dont on ne sait rien — pas de code, code
 * inattendu — doit remonter. Seuls les codes explicitement listés comme
 * attendus sont écartés.
 */
export function doitEtreNotifiee(error: unknown): boolean {
  const statusCode = statusCodeDeLErreur(error)

  if (statusCode === undefined) return true

  return !codesAttendus.has(statusCode)
}
